/**
 * CrackedWall — an ATTACK-breakable wall (Brief 014 P1). Hollow Knight's breakable walls:
 * a SOLID you destroy by swinging at it — gated on the player's ATTACK hitbox. (The BRICK
 * is never destructible; this is the only attack-broken solid.)
 *
 * `attackBreakable` marks it for combat.resolveAttack; World routes the break through
 * ctx.killEntity (the shared non-solid path). Optionally hides a `props.reward`, emitted
 * on the first update AFTER the break (that update still runs during the debris window and
 * carries a ctx). On a non-COMBAT archetype no attack hitbox is ever armed → it is simply
 * an indestructible solid wall.
 *
 * Determinism: dt-driven debris timer + a one-shot reward emit → ESLint-clean.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';
import { resolveRewardToken } from './rewards';

const DEBRIS_SECONDS = 0.18;

export class CrackedWall implements Entity {
  readonly type = EntityType.CRACKEDWALL;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;
  readonly attackBreakable = true;

  private reward?: string;
  private rewardCount: number;
  private debrisTimer = 0;
  private pendingReward = false;

  /** @param props `reward` (optional pickup token), `count` (default 1). */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    this.body = { x: cx - ts / 2, y: feetY - ts, w: ts, h: ts };
    this.reward = typeof props?.reward === 'string' ? props.reward : undefined;
    const c = props?.count;
    this.rewardCount = typeof c === 'number' && c > 0 ? Math.floor(c) : 1;
  }

  /** Solid until broken; a broken wall stops colliding. */
  solidBody(): AABB | null { return this.alive ? this.body : null; }

  update(dt: number, ctx: EntityContext): void {
    if (this.pendingReward) {
      this.pendingReward = false;
      if (this.reward) {
        const { type, props } = resolveRewardToken(this.reward);
        const cx = this.body.x + this.body.w / 2;
        for (let i = 0; i < this.rewardCount; i++) ctx.spawnEntityAt(type, cx + i * 10, this.body.y, props);
      }
    }
    if (!this.alive) this.debrisTimer = Math.max(0, this.debrisTimer - dt);
  }

  /** Kill hook (ctx.killEntity from the attack pass) — go non-solid + open the debris window. */
  onKilled(): void {
    if (!this.alive) return;
    this.alive = false;
    this.debrisTimer = DEBRIS_SECONDS;
    this.pendingReward = this.reward != null;
  }

  onPlayerOverlap(_ctx: EntityContext): void { /* solid — broken via the attack pass */ }

  spriteKey(): string { return 'crackedwall'; }
  visible(): boolean { return this.alive || this.debrisTimer > 0; }
}
